<script setup lang="ts">
const props = defineProps({
  title: {
    type: String,
    default: "",
  },
  items: {
    type: Array as () => Array<{ title?: string; text?: string }>,
    default: () => [],
  },
  defaultOpenFirst: {
    type: Boolean,
    default: false,
  },
});

const openIndex = ref<number | null>(props.defaultOpenFirst ? 0 : null);

function toggle(i: number) {
  openIndex.value = openIndex.value === i ? null : i;
}

function panelId(i: number) {
  return `acc-panel-${i}`;
}

function buttonId(i: number) {
  return `acc-btn-${i}`;
}
</script>

<template>
  <section class="content-accordion">
    <h2 v-if="title" class="mb-md text-2xl font-bold">
      {{ title }}
    </h2>

    <div class="border border-gray-darker">
      <div
        v-for="(acc, i) in items"
        :key="`${acc.title ?? 'item'}-${i}`"
        class="border-b border-gray-darker last:border-b-0"
      >
        <button
          type="button"
          class="w-full flex items-center justify-between p-sm text-left hover:bg-gray-darkest focus:outline-offset-4 focus:outline-orange-dark focus:outline-4"
          :id="buttonId(i)"
          :aria-controls="panelId(i)"
          :aria-expanded="openIndex === i ? 'true' : 'false'"
          @click="toggle(i)"
        >
          <span class="font-bold">
            {{ acc.title }}
          </span>

          <span
            class="nav-chevron inline-flex transition-transform duration-200"
            :class="{ 'rotate-180': openIndex === i }"
            aria-hidden="true"
          >
            <IconChevronDown />
          </span>
        </button>

        <!-- Animated panel -->
        <div
          class="acc-panel border-t border-gray-darker"
          :class="{ 'is-open': openIndex === i }"
          role="region"
          :id="panelId(i)"
          :aria-labelledby="buttonId(i)"
        >
          <div class="p-sm">
            <div class="prose prose--max-w" v-html="acc.text"></div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Small, clean open/close animation */
.acc-panel {
  overflow: hidden;
  max-height: 0;
  opacity: 0;
  transition: max-height 220ms ease, opacity 160ms ease;
}

.acc-panel.is-open {
  max-height: 800px; /* pick a value that comfortably fits your content */
  opacity: 1;
}
</style>
